import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { t } from "ttag";

import { ROUTES } from "@/app/router";
import { consultationsQueriesStore } from "@/features/consultations/api";
import { useConsultationStore } from "@/features/consultations/contexts";
import { APPOINTMENT_TYPE } from "@/features/consultations/models";
import { useCurrentMedicationRequests } from "@/features/my-meds/hooks";
import { MY_MEDS_ROUTE } from "@/features/my-meds/router";
import { CATEGORY_SLUG } from "@/shared/domains";
import { usePageTitle } from "@/shared/hooks";
import { CONSULTATION_PURPOSE } from "@/shared/models/legacy-constants";
import { Alert, Button, Card } from "@/shared/ui";
import { PurposeOptionsSkeleton } from "./components";
import { ConsultationExistsAlert, MedRequestExistsAlert } from "./modals";
import { PurposeHeader, PurposeOptions } from "./sections";

export const ConsultationPurpose = () => {
  usePageTitle(t`Consultation purpose`);

  const navigate = useNavigate();

  const purpose = useConsultationStore((state) => state.purpose);
  const setPurpose = useConsultationStore((state) => state.setPurpose);

  const [showMedRequestExistsAlert, setShowMedRequestExistsAlert] =
    useState(false);
  const [showConsultationExistsAlert, setShowConsultationExistsAlert] =
    useState(false);

  const {
    data: upcomingMedicationRequestConsultations,
    isLoading: isLoadingUpcomingConsultations,
  } = useQuery(
    consultationsQueriesStore.consultations.appointments._ctx.list._ctx.upcoming
      ._ctx.medicationRequest,
  );

  const { data: needsSyncData, isLoading: isLoadingNeedsSyncRefill } = useQuery(
    consultationsQueriesStore.consultations.patients._ctx.needsSyncRefill,
  );

  const {
    data: currentMedicationRequests,
    isLoading: isLoadingCurrentMedicationRequest,
  } = useCurrentMedicationRequests();

  const isLoading =
    isLoadingNeedsSyncRefill ||
    isLoadingUpcomingConsultations ||
    isLoadingCurrentMedicationRequest;

  const hasPendingMedicationRequest = currentMedicationRequests?.some(
    (request) =>
      request.product.category.slug === CATEGORY_SLUG.WEIGHT_LOSS ||
      request.product.category.slug === CATEGORY_SLUG.MICRODOSING,
  );

  const hasUpcomingNurseConsultation =
    upcomingMedicationRequestConsultations?.some(
      (consultation) => consultation.type === APPOINTMENT_TYPE.OTHER,
    );

  const hasUpcomingFollowUpConsultation =
    upcomingMedicationRequestConsultations?.some(
      (consultation) =>
        consultation.type === APPOINTMENT_TYPE.MEDICATION_REQUEST_INITIAL ||
        consultation.type === APPOINTMENT_TYPE.MEDICATION_REQUEST_FOLLOW_UP,
    );

  const isRefillDisabled =
    isLoading ||
    Boolean(hasPendingMedicationRequest) ||
    Boolean(needsSyncData?.needsSync);

  useEffect(() => {
    setPurpose(
      isRefillDisabled
        ? CONSULTATION_PURPOSE.CONSULTATION
        : CONSULTATION_PURPOSE.REFILL,
    );
  }, [isRefillDisabled, setPurpose]);

  useEffect(() => {
    if (!isLoading && hasUpcomingNurseConsultation) {
      if (hasPendingMedicationRequest) {
        setShowMedRequestExistsAlert(true);
      } else if (hasUpcomingFollowUpConsultation) {
        setShowConsultationExistsAlert(true);
        return;
      }
    }
  }, [
    isLoading,
    hasUpcomingNurseConsultation,
    hasPendingMedicationRequest,
    hasUpcomingFollowUpConsultation,
  ]);

  const handleNext = () => {
    if (purpose === CONSULTATION_PURPOSE.REFILL) {
      navigate(MY_MEDS_ROUTE.REFILL_REQUEST);
    } else {
      navigate(ROUTES.CONSULTATIONS.FOLLOW_UP);
    }
  };

  return (
    <main className="flex w-full flex-col gap-6 px-4 pt-4 md:px-8 md:pt-12 lg:px-16">
      <MedRequestExistsAlert open={showMedRequestExistsAlert} />
      <ConsultationExistsAlert open={showConsultationExistsAlert} />

      <PurposeHeader />

      <div className="flex flex-col gap-4 pb-8">
        <Card.Root>
          <Card.Header>
            <Card.Title>
              {t`Select the option that best applies to you`}
            </Card.Title>
          </Card.Header>
          <Card.Content>
            {isLoading ? (
              <PurposeOptionsSkeleton />
            ) : (
              <PurposeOptions
                isRefillDisabled={isRefillDisabled}
                isAsyncDisabledForTirzepatide={
                  needsSyncData?.needsSync ?? false
                }
              />
            )}
          </Card.Content>
        </Card.Root>

        {purpose === CONSULTATION_PURPOSE.REFILL && (
          <Alert>
            {t`You will be able to review your medication request by visiting the “Your Meds” section`}
          </Alert>
        )}
        {purpose === CONSULTATION_PURPOSE.CONSULTATION &&
          needsSyncData?.needsSync && (
            <Alert>
              <h6 className="font-semibold">{t`Video visit required`}</h6>
              <p>{t`A video visit with a provider is required before your next refill can be processed.`}</p>
            </Alert>
          )}

        <div className="flex flex-col-reverse items-stretch justify-end gap-4 md:flex-row md:items-center">
          <Button
            size="lg"
            onClick={() => navigate(ROUTES.CONSULTATIONS.OVERVIEW)}
            variant="secondary"
          >
            {t`Cancel`}
          </Button>
          <Button
            size="lg"
            disabled={isLoading || !purpose}
            onClick={handleNext}
          >
            {t`Next`}
          </Button>
        </div>
      </div>
    </main>
  );
};
